import { DataGridColumn, DataType, QueryFilter, SourceType } from "../../../../../../models/queries/queries.models";
import { LookupValue, LookupValueRequest } from "../../../../../../models/lookups/lookup.models";
import { BaseRuleConfigParameterModel } from "../../../../../../models/customers/customer.models";
import { LookupValueQuery } from "../../../../../../constants/modules/lookup.constants";
import { LookupClientService } from "../../../../../../services/lookups/lookup-client.service";
import { EventAggregator } from "aurelia-event-aggregator";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { RightShipDialogServiceConstants } from "../../../../../../constants/basics/rightship-dialog.constants";
import { CustomGridPageBase } from "../../../../common/custom-grid-page.base";
import { I18N } from "aurelia-i18n";
import { BindingEngine, observable } from "aurelia-binding";
import { VettingType } from "../../../../../../models/vetting/vetting.models";
import { Guid } from "../../../../../../utilities/guid";

export abstract class BaseRuleParameters<T extends BaseRuleConfigParameterModel> extends CustomGridPageBase {

    ruleConfigParameters: LookupValue[] = [];
    isReadOnly: boolean = false;
    customerId: number;
    businessUnitId: number;
    propName: string;
    configParameters: T[] = [];
    public isMobileFilterOpen: boolean;
    public vettingRulesFilterSelected: boolean = false;
    public criticalRulesFilterSelected: boolean = false;
    public softRulesFilterSelected: boolean = false;
    public vetAutoScrenningRulesFilterSelected: boolean = false;
    public showColumnSorting: boolean

    async activate(model: any, configParameters: T[],  propName: string) {
        this.customerId = model && model.customer ? model.customer.customerId : null;
        this.businessUnitId = model && model.businessUnit ? model.businessUnit.businessUnitId : null;
        this.isReadOnly = model && Object.keys(model).indexOf("isReadOnly") > -1 && model.isReadOnly == true;
        const request = new LookupValueRequest();
        request.lookupValueQuery = LookupValueQuery.RuleConfigParameters;
        this.ruleConfigParameters = (await this.lookupValueService.getLookup(request)).allValuesIncludingInactive;
        this.configParameters = configParameters;
        this.propName = propName;
        this.reload();
    }

    reload() {
        if (this.configParameters) {
            const filters = [];
            if (this.customerId)
                filters.push(QueryFilter.getQuery("customerId", this.customerId, "eq", DataType.Number));
            if (this.businessUnitId)
                filters.push(QueryFilter.getQuery("businessUnitId", this.businessUnitId, "eq", DataType.Number));

            if (this.searchFilter) {
                const guid = Guid.generateGuid();
                filters.push(QueryFilter.getQuery("name", this.searchFilter, "contains({0}, '{1}')", DataType.Default, true, false, guid, true, true));
                filters.push(QueryFilter.getQuery("vettingType", this.searchFilter, "contains({0}, '{1}')", DataType.Default, false, true, guid, true, true));
            }

            let filteTriggered = false;
            if (this.softRulesFilterSelected) {
                filters.push(QueryFilter.getQuery("isSoftRule", true, "eq", DataType.Boolean, !filteTriggered, false, "rulesGroup", true));
                filteTriggered = true;
            }
            if (this.criticalRulesFilterSelected) {
                filters.push(QueryFilter.getQuery("isCriticalRule", true, "eq", DataType.Boolean, !filteTriggered, false, "rulesGroup", true));
                filteTriggered = true;
            }
            if (this.vettingRulesFilterSelected) {
                filters.push(QueryFilter.getQuery("isVettingRule", true, "eq", DataType.Boolean, !filteTriggered, false, "rulesGroup", true));
                filteTriggered = true;
            }
            if (this.vetAutoScrenningRulesFilterSelected) {
                filters.push(QueryFilter.getQuery("isVetAutoScreeningRule", true, "eq", DataType.Boolean, !filteTriggered, false, "rulesGroup", true));
                filteTriggered = true;
            }

            this.itemSource = {
                source: `/odata/${this.getODataUrl()}`,
                sourceType: SourceType.ODataUrl,
                columns: this.getColumns(),
                initialFilters: filters
            };
        }
    }

    abstract getODataUrl(): string;

    searchFilterChanged() {
        this.reload()
    }

    @observable
    searchFilter: string = "";

    clearSearchFilter() {
        this.searchFilter = "";
    }

    getColumns(): DataGridColumn[] {
        return [
            {
                sortOrder: 1,
                columnOrder: 1,
                name: "name",
                dataType: DataType.Default,
                title: "Name",
                isFilterable: false,
                disableSorting: true,
                isSortable: false
            },
            {
                sortOrder: 2,
                columnOrder: 2,
                name: "vettingType",
                dataType: DataType.Default,
                title: "Vetting Type",
                isFilterable: false,
                disableSorting: true,
                isSortable: false
            },
            {
                name: "",
                sortOrder: 100,
                columnOrder: 100,
                title: this.isReadOnly ? "View" : "Edit",
                instancePerRow: true,
                dataType: DataType.CustomTemplate,
                value: `<div class="column">
                            <a class="link" click.trigger="openRulesDialog(item)">${(this.isReadOnly ? "view" : "edit")}</a>
                        </div>`,
                parameter: {
                    item: null,
                    openRulesDialog: (x) => {
                        const ruleConfigParameterId = x.ruleConfigParameterId;
                        this.beforeEdit(ruleConfigParameterId, x.isVettingRule, x.isCriticalRule, x.isSoftRule);
                        const items = this.configParameters.filter(x => x.ruleConfigParameterId == ruleConfigParameterId);
                        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.EditRuleParametersDialog, {
                            isReadOnly: this.isReadOnly,
                            parameters: items,
                            isVettingRule: x.isVettingRule,
                            isCriticalRule: x.isCriticalRule,
                            isSoftRule: x.isSoftRule,
                            ruleConfigParameters: this.ruleConfigParameters,
                            businessUnitId: this.businessUnitId,
                            customerId: this.customerId
                        }, true, false, false, "right");
                    }
                }
            }
        ];
    }

    beforeEdit(ruleConfigParameterId: number, isVettingRule: boolean, isCriticalRule: boolean, isSoftRule: boolean) {
        const p = this.configParameters.filter(x => x.ruleConfigParameterId == ruleConfigParameterId);
        const parameter = this.ruleConfigParameters.filter(x => x.id == ruleConfigParameterId)[0];

        if (isVettingRule) {
            const anyVetting = p.filter(x => x.vettingTypeId == VettingType.FullVet).length > 0;
            if (!anyVetting) {
                const item: T = {
                    isCritical: false,
                    vettingTypeId: VettingType.FullVet,
                    ruleConfigParameterId: parameter.id,
                    ruleConfigParameter: parameter,
                    value: null,
                    defaultDisplayText: null,
                    isAdvanced: false,
                    ruleConfigAdvancedParameters: [],
                    useDefault: true,
                    copyVettingRuleParameter: false
                } as T;

                item[this.propName] = 0;
                this.configParameters.push(item);
            }
        }

        if (isCriticalRule) {
            const anyAutoScreeningCritical = p.filter(x => x.vettingTypeId == VettingType.AutoScreening && x.isCritical).length > 0;
            if (!anyAutoScreeningCritical) {
                const item: T = {
                    isCritical: true,
                    vettingTypeId: VettingType.AutoScreening,
                    ruleConfigParameterId: parameter.id,
                    ruleConfigParameter: parameter,
                    value: null,
                    defaultDisplayText: null,
                    isAdvanced: false,
                    ruleConfigAdvancedParameters: [],
                    useDefault: !isVettingRule,
                    copyVettingRuleParameter: isVettingRule
                } as T;

                item[this.propName] = 0;
                this.configParameters.push(item);
            }
        }

        if (isSoftRule) {
            const anyAutoScreeningSoft = p.filter(x => x.vettingTypeId == VettingType.AutoScreening && !x.isCritical).length > 0;
            if (!anyAutoScreeningSoft) {
                const item: T = {
                    isCritical: false,
                    vettingTypeId: VettingType.AutoScreening,
                    ruleConfigParameterId: parameter.id,
                    ruleConfigParameter: parameter,
                    value: null,
                    defaultDisplayText: null,
                    isAdvanced: false,
                    ruleConfigAdvancedParameters: [],
                    useDefault: !isVettingRule,
                    copyVettingRuleParameter: isVettingRule
                } as T;

                item[this.propName] = 0;
                this.configParameters.push(item);
            }
        }
    }


    constructor(protected lookupValueService: LookupClientService, protected eventAggregator: EventAggregator,
        protected dialogClientService: DialogClientService, i18n: I18N, bindingEngine: BindingEngine) { super(i18n, "Rule Parameters", bindingEngine) }


    isValidDate(d: any) {
        return d instanceof Date && !isNaN(<any>d);
    }

    filterVettingRules(isMobile: boolean = false) {
        this.vettingRulesFilterSelected = !this.vettingRulesFilterSelected;
        if (!isMobile) this.reload();
    }

    filterCriticalRules(isMobile: boolean = false) {
        this.criticalRulesFilterSelected = !this.criticalRulesFilterSelected;
        if (!isMobile) this.reload();
    }

    filterSoftRules(isMobile: boolean = false) {
        this.softRulesFilterSelected = !this.softRulesFilterSelected;
        if (!isMobile) this.reload();
    }

    filterVetAutoScreeningRules(isMobile: boolean = false) {
        this.vetAutoScrenningRulesFilterSelected = !this.vetAutoScrenningRulesFilterSelected;
        if (!isMobile) this.reload();
    }

    clearAllFilters(reload: boolean = true) {
        this.vettingRulesFilterSelected = false;
        this.softRulesFilterSelected = false;
        this.criticalRulesFilterSelected = false;
        this.vetAutoScrenningRulesFilterSelected = false;

        if (reload)
            this.reload();
    }
}
